import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { formatInTimeZone } from 'date-fns-tz'
import {
  Box,
  Button,
  Field,
  Flex,
  Input,
  Loading,
  Message,
  Modal,
  Text,
} from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useIndeedFlexShiftActionsContextValue } from '../context/IndeedFlexShiftActionsProvider'
import { useIndeedFlexLongTermAssignmentPreviewQuery } from 'queries/admin/indeedFlex/useIndeedFlexLongTermAssignmentPreviewQuery'
import { SpecificWorkerIdsField } from './SpecificWorkerIdsField'

export const createIndeedFlexLongTermAssignmentFormSchema = z.object({
  assignmentName: z.string().trim().min(1, 'Assignment name is required'),
  specificWorkerIds: z.array(z.number().int().positive()).default([]),
})

export type CreateIndeedFlexLongTermAssignmentForm = z.infer<
  typeof createIndeedFlexLongTermAssignmentFormSchema
>

type CreateIndeedFlexLongTermAssignmentButtonProps = {
  ifClientId: number
  ifJobId: number
}

export const CreateIndeedFlexLongTermAssignmentButton = ({
  ifClientId,
  ifJobId,
}: CreateIndeedFlexLongTermAssignmentButtonProps) => {
  const {
    actions: {
      createIndeedFlexLongTermAssignment,
      resetCreateIndeedFlexLongTermAssignment,
    },
    state: { createIndeedFlexLongTermAssignmentMutationState },
  } = useIndeedFlexShiftActionsContextValue()

  const [isOpen, setIsOpen] = useState(false)

  const {
    data: previewData,
    isPending: isPreviewPending,
    isError: isPreviewError,
    error: previewError,
  } = useIndeedFlexLongTermAssignmentPreviewQuery({
    ifClientId,
    ifJobId,
    enabled: isOpen,
  })

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateIndeedFlexLongTermAssignmentForm>({
    resolver: zodResolver(createIndeedFlexLongTermAssignmentFormSchema),
    defaultValues: {
      assignmentName: '',
      specificWorkerIds: [],
    },
  })

  useEffect(() => {
    if (!createIndeedFlexLongTermAssignmentMutationState.isSuccess) {
      return
    }

    setIsOpen(false)
    reset()
    resetCreateIndeedFlexLongTermAssignment()
  }, [
    createIndeedFlexLongTermAssignmentMutationState.isSuccess,
    reset,
    resetCreateIndeedFlexLongTermAssignment,
  ])

  const handleClick = () => {
    resetCreateIndeedFlexLongTermAssignment()
    reset()
    setIsOpen(true)
  }

  const handleCancel = () => {
    resetCreateIndeedFlexLongTermAssignment()
    reset()
    setIsOpen(false)
  }

  const onSubmit = handleSubmit((data) => {
    createIndeedFlexLongTermAssignment({
      ifClientId,
      ifJobId,
      assignmentName: data.assignmentName,
      specificWorkerIds:
        data.specificWorkerIds.length > 0 ? data.specificWorkerIds : undefined,
    })
  })

  const previewShifts = previewData?.items ?? []
  const canSubmit =
    !isPreviewPending &&
    !isPreviewError &&
    previewShifts.length > 0 &&
    !createIndeedFlexLongTermAssignmentMutationState.isPending

  return (
    <>
      <Button
        variant="secondary"
        kind="small"
        onClick={handleClick}
        style={{ padding: '0 0.5rem' }}
        disabled={createIndeedFlexLongTermAssignmentMutationState.isPending}
        loading={createIndeedFlexLongTermAssignmentMutationState.isPending}
      >
        <Text fontSize="12px">Create long term assignment</Text>
      </Button>
      {isOpen ? (
        <Modal
          open
          onClose={handleCancel}
          title={`Create long term assignment for job ${ifJobId}`}
        >
          <Text mb={2}>
            Creates a WorkWhile assignment with all future uncreated shifts for
            this Indeed Flex job.
          </Text>
          {isPreviewPending ? <Loading /> : null}
          {isPreviewError ? (
            <Message variant="error" mb={3}>
              {parseErrorMessage(previewError)}
            </Message>
          ) : null}
          {!isPreviewPending &&
          !isPreviewError &&
          previewShifts.length === 0 ? (
            <Message variant="error" mb={3}>
              No future uncreated shifts found for this Indeed Flex job.
            </Message>
          ) : null}
          {!isPreviewPending && !isPreviewError && previewShifts.length > 0 ? (
            <Box mb={3}>
              <Text fontWeight="bold" mb={2}>
                Shifts to create ({previewShifts.length})
              </Text>
              <Flex flexDirection="column" gap={2}>
                {previewShifts.map((shift) => (
                  <Box
                    key={shift.ifShiftId}
                    p={2}
                    style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 4,
                    }}
                  >
                    <Text fontSize={1}>
                      Shift {shift.ifShiftId} · {shift.workersNeeded} workers
                    </Text>
                    <Text fontSize={0}>
                      {formatInTimeZone(
                        shift.startTime,
                        shift.timezone,
                        'EEEE, MMM d, h:mm a z'
                      )}{' '}
                      –{' '}
                      {formatInTimeZone(
                        shift.endTime,
                        shift.timezone,
                        'EEEE, MMM d, h:mm a z'
                      )}
                    </Text>
                  </Box>
                ))}
              </Flex>
            </Box>
          ) : null}
          {createIndeedFlexLongTermAssignmentMutationState.isError &&
          createIndeedFlexLongTermAssignmentMutationState.errorMessage ? (
            <Message variant="error" mb={3}>
              {createIndeedFlexLongTermAssignmentMutationState.errorMessage}
            </Message>
          ) : null}
          <form onSubmit={onSubmit}>
            <Field
              label="Assignment name"
              error={errors.assignmentName?.message}
            >
              <Input {...register('assignmentName')} />
            </Field>
            <SpecificWorkerIdsField
              control={control}
              name="specificWorkerIds"
              errorMessage={errors.specificWorkerIds?.message}
            />
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              gap={2}
              mt={3}
            >
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={
                  createIndeedFlexLongTermAssignmentMutationState.isPending
                }
                disabled={!canSubmit}
              >
                Submit
              </Button>
            </Flex>
          </form>
        </Modal>
      ) : null}
    </>
  )
}
